import React, { useState, useEffect } from 'react';
import { FaStore, FaTimes, FaSave, FaArrowRight, FaArrowLeft, FaMapMarkerAlt } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { stationService } from '../../services/refuelService';
import { userService } from '../../services/userService';
import { FuelStation, StationRequest } from '../../types';
import MapPicker, { LocationData } from '../MapPicker';
import OpeningHoursEditor, { hasOpeningHours } from '../OpeningHoursEditor';
import StationPhotoField, { noPhotoChange, PhotoChange, savePhotoChange } from '../StationPhotoField';

interface AdminStationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  station: FuelStation | null;
}

type Step = 1 | 2 | 3;

const initialFormState: StationRequest = {
  nomePosto: '',
  cnpj: '',
  logradouro: '',
  numero: '',
  complemento: '',
  bairro: '',
  cidade: '',
  estado: '',
  cep: '',
  telefone: '',
  email: '',
  aberto24h: false,
  horarios: [],
  latitude: undefined,
  longitude: undefined,
  exigeEnchimentoCompleto: false,
  proprietarioId: undefined
};

const AdminStationModal: React.FC<AdminStationModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  station
}) => {
  const [currentStep, setCurrentStep] = useState<Step>(1);
  const [formData, setFormData] = useState<StationRequest>(initialFormState);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [users, setUsers] = useState<any[]>([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [photo, setPhoto] = useState<PhotoChange>(noPhotoChange);

  useEffect(() => {
    if (isOpen) {
      loadUsers();
      setCurrentStep(1);
      setPhoto(noPhotoChange);
      if (station) {
        // Preencher formulário para edição
        setFormData({
          nomePosto: station.nomePosto,
          cnpj: station.cnpj || '',
          logradouro: station.logradouro || '',
          numero: station.numero || '',
          complemento: station.complemento || '',
          bairro: station.bairro || '',
          cidade: station.cidade || '',
          estado: station.estado || '',
          cep: station.cep || '',
          telefone: station.telefone || '',
          email: station.email || '',
          aberto24h: !!station.aberto24h,
          horarios: station.horarios || [],
          latitude: station.latitude,
          longitude: station.longitude,
          exigeEnchimentoCompleto: station.exigeEnchimentoCompleto || false,
          proprietarioId: station.parceiroId || station.parceiro?.id
        });
      } else {
        setFormData(initialFormState);
      }
    }
  }, [isOpen, station]);

  const loadUsers = async () => {
    setIsLoadingUsers(true);
    try {
      const response = await userService.getAllUsers();
      if (response.success && response.data) {
        const partners = response.data.filter(user => user.nivelAcesso === 'PARCEIRO');
        setUsers(partners);
      }
    } catch (error) {
      console.error('Erro ao carregar usuários:', error);
    } finally {
      setIsLoadingUsers(false);
    }
  };

  const handleSubmit = async () => {
    if (currentStep < 3) {
      if (validateStep(currentStep)) {
        setCurrentStep((prevStep) => (prevStep + 1) as Step);
      }
      return;
    }

    if (!formData.proprietarioId) {
      toast.error('Selecione o parceiro responsável pelo posto');
      return;
    }

    setIsSubmitting(true);

    try {
      if (station) {
        const response = await stationService.updateStation(station.id, formData);
        if (response.success) {
          await savePhotoChange(station.id, photo);
          toast.success('Posto atualizado com sucesso!');
          onSuccess();
          onClose();
        }
      } else {
        const response = await stationService.createStation(formData);
        if (response.success) {
          await savePhotoChange(response.data.id, photo);
          toast.success('Posto cadastrado com sucesso!');
          onSuccess();
          onClose();
        }
      }
    } catch (error: any) {
      console.error('Erro ao salvar posto:', error);
      toast.error(error.response?.data?.message || 'Erro ao salvar posto');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleLocationSelect = (location: LocationData) => {
    setFormData({
      ...formData,
      latitude: location.latitude,
      longitude: location.longitude
    });
  };

  const handleNextStep = () => {
    if (currentStep < 3) {
      setCurrentStep((prevStep) => (prevStep + 1) as Step);
    }
  };

  const handlePreviousStep = () => {
    if (currentStep > 1) {
      setCurrentStep((prevStep) => (prevStep - 1) as Step);
    }
  };

  const validateStep = (step: Step): boolean => {
    switch (step) {
      case 1:
        // Dados gerais - nome e horário de funcionamento são obrigatórios
        if (formData.nomePosto.trim() === '') return false;
        if (!hasOpeningHours(formData.aberto24h, formData.horarios)) {
          toast.error('Informe o horário de funcionamento');
          return false;
        }
        return true;
      case 2:
        // Localização - não obrigatório
        return true;
      case 3:
        // Combustíveis e descontos - não obrigatório
        return true;
      default:
        return true;
    }
  };

  const canProceed = validateStep(currentStep);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-gradient-to-br from-[#1a2332] to-[#253547] border border-white/10 rounded-2xl p-8 max-w-4xl w-full my-8">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-white flex items-center gap-3">
            <FaStore className="text-[#089453]" />
            {station ? 'Editar Posto' : 'Cadastrar Novo Posto'}
          </h2>
          <button
            onClick={onClose}
            className="text-[#888] hover:text-white transition-colors"
          >
            <FaTimes size={24} />
          </button>
        </div>

        {/* Progress Steps */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center flex-1">
            <div className={`flex items-center justify-center w-10 h-10 rounded-full font-bold ${currentStep >= 1 ? 'bg-[#089453] text-white' : 'bg-[#0f1419] text-[#666]'}`}>
              1
            </div>
            <div className="flex-1 mx-2">
              <div className={`h-1 rounded ${currentStep >= 2 ? 'bg-[#089453]' : 'bg-[#0f1419]'}`}></div>
            </div>
          </div>
          <div className="flex items-center flex-1">
            <div className={`flex items-center justify-center w-10 h-10 rounded-full font-bold ${currentStep >= 2 ? 'bg-[#089453] text-white' : 'bg-[#0f1419] text-[#666]'}`}>
              2
            </div>
            <div className="flex-1 mx-2">
              <div className={`h-1 rounded ${currentStep >= 3 ? 'bg-[#089453]' : 'bg-[#0f1419]'}`}></div>
            </div>
          </div>
          <div className={`flex items-center justify-center w-10 h-10 rounded-full font-bold ${currentStep >= 3 ? 'bg-[#089453] text-white' : 'bg-[#0f1419] text-[#666]'}`}>
            3
          </div>
        </div>

        {/* Step Labels */}
        <div className="flex items-center justify-between mb-6 px-4">
          <span className={`text-sm font-medium ${currentStep === 1 ? 'text-white' : 'text-[#666]'}`}>
            Dados Gerais
          </span>
          <span className={`text-sm font-medium ${currentStep === 2 ? 'text-white' : 'text-[#666]'}`}>
            Localização
          </span>
          <span className={`text-sm font-medium ${currentStep === 3 ? 'text-white' : 'text-[#666]'}`}>
            Próximo Passo
          </span>
        </div>

        <div>
          {/* Step 1: Dados Gerais */}
          {currentStep === 1 && (
            <div className="space-y-4 mb-6">
              <h3 className="text-lg font-bold text-white mb-4 pb-2 border-b border-white/10">
                Informações Básicas
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Nome do Posto */}
                <div className="md:col-span-2">
                  <label className="block text-[#888] text-sm font-medium mb-2">
                    Nome do Posto *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.nomePosto}
                    onChange={(e) => setFormData({ ...formData, nomePosto: e.target.value })}
                    className="w-full px-4 py-3 bg-[#0f1419] border border-white/10 rounded-xl text-white focus:outline-none focus:border-[#089453]"
                    placeholder="Ex: Posto Estrela"
                  />
                </div>

                {/* CNPJ */}
                <div>
                  <label className="block text-[#888] text-sm font-medium mb-2">
                    CNPJ
                  </label>
                  <input
                    type="text"
                    value={formData.cnpj}
                    onChange={(e) => setFormData({ ...formData, cnpj: e.target.value })}
                    className="w-full px-4 py-3 bg-[#0f1419] border border-white/10 rounded-xl text-white focus:outline-none focus:border-[#089453]"
                    placeholder="00.000.000/0000-00"
                  />
                </div>

                {/* Telefone */}
                <div>
                  <label className="block text-[#888] text-sm font-medium mb-2">
                    Telefone
                  </label>
                  <input
                    type="text"
                    value={formData.telefone}
                    onChange={(e) => setFormData({ ...formData, telefone: e.target.value })}
                    className="w-full px-4 py-3 bg-[#0f1419] border border-white/10 rounded-xl text-white focus:outline-none focus:border-[#089453]"
                    placeholder="(00) 00000-0000"
                  />
                </div>

                {/* Email */}
                <div>
                  <label className="block text-[#888] text-sm font-medium mb-2">
                    Email
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-4 py-3 bg-[#0f1419] border border-white/10 rounded-xl text-white focus:outline-none focus:border-[#089453]"
                    placeholder="contato@posto.com"
                  />
                </div>

                {/* Foto */}
                <div className="md:col-span-2">
                  <label className="block text-[#888] text-sm font-medium mb-2">
                    Foto do posto
                  </label>
                  <StationPhotoField currentUrl={station?.fotoUrl} value={photo} onChange={setPhoto} />
                </div>

                {/* Horário de Funcionamento */}
                <div className="md:col-span-2">
                  <label className="block text-[#888] text-sm font-medium mb-2">
                    Horário de Funcionamento *
                  </label>
                  <OpeningHoursEditor
                    aberto24h={!!formData.aberto24h}
                    horarios={formData.horarios || []}
                    onChange={(aberto24h, horarios) => setFormData({ ...formData, aberto24h, horarios })}
                  />
                </div>

                {/* Proprietário */}
                <div className="md:col-span-2">
                  <label className="block text-[#888] text-sm font-medium mb-2">
                    Proprietário (Parceiro)
                  </label>
                  <select
                    value={formData.proprietarioId || ''}
                    onChange={(e) => setFormData({ ...formData, proprietarioId: e.target.value || undefined })}
                    className="w-full px-4 py-3 bg-[#0f1419] border border-white/10 rounded-xl text-white focus:outline-none focus:border-[#089453]"
                    required
                    disabled={isLoadingUsers}
                  >
                    <option value="">Selecione um parceiro</option>
                    {users.map((user) => (
                      <option key={user.id} value={user.id}>
                        {user.fullName} - {user.email}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Localização */}
          {currentStep === 2 && (
            <div className="space-y-4 mb-6">
              <h3 className="text-lg font-bold text-white mb-4 pb-2 border-b border-white/10">
                Endereço e Localização
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* CEP */}
                <div>
                  <label className="block text-[#888] text-sm font-medium mb-2">
                    CEP
                  </label>
                  <input
                    type="text"
                    value={formData.cep}
                    onChange={(e) => setFormData({ ...formData, cep: e.target.value })}
                    className="w-full px-4 py-3 bg-[#0f1419] border border-white/10 rounded-xl text-white focus:outline-none focus:border-[#089453]"
                    placeholder="00000-000"
                  />
                </div>

                {/* Logradouro */}
                <div className="md:col-span-2">
                  <label className="block text-[#888] text-sm font-medium mb-2">
                    Logradouro
                  </label>
                  <input
                    type="text"
                    value={formData.logradouro}
                    onChange={(e) => setFormData({ ...formData, logradouro: e.target.value })}
                    className="w-full px-4 py-3 bg-[#0f1419] border border-white/10 rounded-xl text-white focus:outline-none focus:border-[#089453]"
                    placeholder="Rua, Avenida, etc"
                  />
                </div>

                {/* Número */}
                <div>
                  <label className="block text-[#888] text-sm font-medium mb-2">
                    Número
                  </label>
                  <input
                    type="text"
                    value={formData.numero}
                    onChange={(e) => setFormData({ ...formData, numero: e.target.value })}
                    className="w-full px-4 py-3 bg-[#0f1419] border border-white/10 rounded-xl text-white focus:outline-none focus:border-[#089453]"
                    placeholder="123"
                  />
                </div>

                {/* Complemento */}
                <div>
                  <label className="block text-[#888] text-sm font-medium mb-2">
                    Complemento
                  </label>
                  <input
                    type="text"
                    value={formData.complemento}
                    onChange={(e) => setFormData({ ...formData, complemento: e.target.value })}
                    className="w-full px-4 py-3 bg-[#0f1419] border border-white/10 rounded-xl text-white focus:outline-none focus:border-[#089453]"
                    placeholder="Sala, Bloco, etc"
                  />
                </div>

                {/* Bairro */}
                <div>
                  <label className="block text-[#888] text-sm font-medium mb-2">
                    Bairro
                  </label>
                  <input
                    type="text"
                    value={formData.bairro}
                    onChange={(e) => setFormData({ ...formData, bairro: e.target.value })}
                    className="w-full px-4 py-3 bg-[#0f1419] border border-white/10 rounded-xl text-white focus:outline-none focus:border-[#089453]"
                    placeholder="Centro"
                  />
                </div>

                {/* Cidade */}
                <div>
                  <label className="block text-[#888] text-sm font-medium mb-2">
                    Cidade
                  </label>
                  <input
                    type="text"
                    value={formData.cidade}
                    onChange={(e) => setFormData({ ...formData, cidade: e.target.value })}
                    className="w-full px-4 py-3 bg-[#0f1419] border border-white/10 rounded-xl text-white focus:outline-none focus:border-[#089453]"
                    placeholder="São Paulo"
                  />
                </div>

                {/* Estado */}
                <div>
                  <label className="block text-[#888] text-sm font-medium mb-2">
                    Estado
                  </label>
                  <input
                    type="text"
                    value={formData.estado}
                    onChange={(e) => setFormData({ ...formData, estado: e.target.value })}
                    className="w-full px-4 py-3 bg-[#0f1419] border border-white/10 rounded-xl text-white focus:outline-none focus:border-[#089453]"
                    placeholder="SP"
                    maxLength={2}
                  />
                </div>
              </div>

              {/* Mapa para seleção de coordenadas */}
              <div className="mt-6">
                <label className="block text-[#888] text-sm font-medium mb-3 flex items-center gap-2">
                  <FaMapMarkerAlt className="text-[#089453]" />
                  Selecione a localização no mapa
                </label>
                <div className="rounded-xl overflow-hidden border border-white/10">
                  <MapPicker
                    initialLocation={
                      formData.latitude && formData.longitude
                        ? { latitude: formData.latitude, longitude: formData.longitude }
                        : undefined
                    }
                    onLocationSelect={handleLocationSelect}
                    height={400}
                    showSearch={true}
                  />
                </div>
                {formData.latitude && formData.longitude && (
                  <p className="text-xs text-[#888] mt-2">
                    Coordenadas: {formData.latitude.toFixed(6)}, {formData.longitude.toFixed(6)}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Step 3: Configuração por combustível */}
          {currentStep === 3 && (
            <div className="space-y-4 mb-6">
              <h3 className="text-lg font-bold text-white mb-4 pb-2 border-b border-white/10">
                Configuração de Combustíveis
              </h3>
              <div className="mt-6 p-4 bg-[#089453]/10 border border-[#089453]/20 rounded-xl">
                <p className="text-sm text-[#84cc34]">
                  Os preços, cashback, desconto por litro, lucro da plataforma e litros mínimos agora são configurados em cada combustível. Salve o posto e use o botão "Combustíveis" no card do posto.
                </p>
              </div>
            </div>
          )}

          {/* Navigation Buttons */}
          <div className="flex gap-4 mt-8 pt-6 border-t border-white/10">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-3 bg-[#0f1419] border border-white/10 text-white rounded-xl hover:bg-white/5 transition-all duration-300"
            >
              Cancelar
            </button>

            <div className="flex-1"></div>

            {currentStep > 1 && (
              <button
                type="button"
                onClick={handlePreviousStep}
                className="px-6 py-3 bg-[#0f1419] border border-white/10 text-white rounded-xl hover:bg-white/5 transition-all duration-300 flex items-center gap-2"
              >
                <FaArrowLeft />
                Anterior
              </button>
            )}

            {currentStep < 3 ? (
              <button
                type="button"
                onClick={handleNextStep}
                disabled={!canProceed}
                className="px-6 py-3 bg-gradient-to-r from-[#089453] to-[#84cc34] text-white font-medium rounded-xl hover:shadow-lg hover:shadow-[#089453]/30 transition-all duration-300 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Próximo
                <FaArrowRight />
              </button>
            ) : (
              <button
                type="button"
                onClick={handleSubmit}
                disabled={isSubmitting || !canProceed}
                className="px-6 py-3 bg-gradient-to-r from-[#089453] to-[#84cc34] text-white font-medium rounded-xl hover:shadow-lg hover:shadow-[#089453]/30 transition-all duration-300 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white/20 border-t-white rounded-full animate-spin" />
                    Salvando...
                  </>
                ) : (
                  <>
                    <FaSave />
                    {station ? 'Atualizar Posto' : 'Cadastrar Posto'}
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminStationModal;
